import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import apiClient from "../api/config";
import { AUTH_ENDPOINTS } from "../api/endpoints";
import {
  clearAuth,
  getRefreshToken,
  setToken,
  setRefreshToken,
} from "../utils/auth";
import socketService from "../services/socketService";

/**
 * Single in-flight refresh promise shared across the whole app.
 * If several requests fail with an expired token at the same time, they all
 * await this ONE refresh call instead of each firing their own (token stampede).
 * It is reset to null once the refresh settles so the next expiry can refresh again.
 */
let refreshPromise = null;

const refreshAccessToken = () => {
  if (!refreshPromise) {
    const storedRefreshToken = getRefreshToken();
    if (!storedRefreshToken) {
      return Promise.reject(new Error("No refresh token available"));
    }

    // Use a bare axios call (NOT apiClient) so this request skips the
    // interceptors below and can never trigger a refresh-of-a-refresh loop.
    refreshPromise = axios
      .post(`${apiClient.defaults.baseURL}${AUTH_ENDPOINTS.REFRESH_TOKEN}`, {
        refreshToken: storedRefreshToken,
      })
      .then(({ data }) => {
        setToken(data.token);
        setRefreshToken(data.refreshToken);
        return data.token;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

/**
 * Custom hook to setup axios interceptors with navigation
 * This allows axios interceptors to use React Router's navigate
 */
const useAxiosInterceptor = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Response interceptor — on an expired access token, refresh once and
    // retry the original request. Only log out if the refresh itself fails.
    const responseInterceptor = apiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const status = error.response?.status;

        // Backend signals an expired/invalid access token as either a 401, or
        // a 400 carrying { token_expired: true } from the auth middleware.
        const tokenExpired =
          status === 401 ||
          (status === 400 && error.response?.data?.token_expired === true);

        const isRefreshCall = originalRequest?.url?.includes(
          AUTH_ENDPOINTS.REFRESH_TOKEN,
        );

        // Try a one-time refresh + retry.
        if (
          tokenExpired &&
          originalRequest &&
          !originalRequest._retry &&
          !isRefreshCall &&
          getRefreshToken()
        ) {
          originalRequest._retry = true;
          try {
            const newToken = await refreshAccessToken();
            originalRequest.headers["x-access-token"] = newToken;
            return apiClient(originalRequest);
          } catch (refreshError) {
            // Refresh token is invalid/expired/revoked — session is over.
            clearAuth(socketService);
            sessionStorage.removeItem("sessionId");
            navigate("/login", { replace: true });
            return Promise.reject(refreshError);
          }
        }

        // Unrecoverable auth failure (no refresh token, retry already failed,
        // or the refresh endpoint itself rejected) — log out.
        if (tokenExpired) {
          clearAuth(socketService);
          sessionStorage.removeItem("sessionId");
          navigate("/login", { replace: true });
        }

        // Handle network errors
        if (!error.response && error.code === "ERR_NETWORK") {
          error.message = "Network error. Please check your connection.";
        }

        return Promise.reject(error);
      },
    );

    // Cleanup interceptor on unmount
    return () => {
      apiClient.interceptors.response.eject(responseInterceptor);
    };
  }, [navigate]);
};

export default useAxiosInterceptor;
